import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_BANK,
  bondPayout,
  formatPence,
  pct,
  weekInterest,
  type BankContent,
  type BankReply,
  type BankView,
  type MoneyLine,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { useCatalogue } from '../lib/content';
import { actionKey, londonClock, londonDay, londonWhen, typedPence } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';

type Tab = 'savings' | 'bonds';
type Bill = { icon: string; label: string; pence: number; dueAt: string; from: string };
const left = (ms: number) => {
  const minutes = Math.max(0, Math.ceil(ms / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  return days ? `${days}d ${hours}h` : hours ? `${hours}h ${minutes % 60}m` : `${minutes % 60}m`;
};

// the Bank, as Lagos Life's 🏦 Bank: your balance and all your money, the reserve bank's savings and bonds, the
// week's bills from everything you run, and the money history
export function BankScreen(props: PhoneAppProps) {
  const catalogue = useCatalogue();
  const content = catalogue?.bank ?? DEFAULT_BANK;
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.bank.mine(), props);
  const bank = reply?.bank ?? null;
  const [history, setHistory] = useState(false);
  return (
    <PhoneAppFrame
      id="bank"
      title={history ? 'Money history' : '🏦 Bank'}
      className="bank-screen"
      closeLabel="Close the bank"
      back={history ? { label: '‹ Bank', onClick: () => setHistory(false) } : undefined}
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={bank ? undefined : 'Opening the vault…'}
    >
      {bank && history && <MoneyHistory />}
      {bank && !history && (
        <div className="business-detail">
          <section className="bank-balance" aria-label="Balance">
            <small>Balance</small>
            <strong>{formatPence(bank.walletPence)}</strong>
            <small>All your money {formatPence(bank.netWorthPence)}</small>
          </section>
          {content.visible ? (
            <Reserve content={content} bank={bank} busy={busy} run={run} />
          ) : (
            <p className="business-muted">The bank opens soon.</p>
          )}
          <WeeklyBills />
          <button type="button" className="bank-history-open" onClick={() => setHistory(true)}>
            <span aria-hidden="true">📜</span>
            <span>
              <strong>Money history</strong>
              <small>Every penny in and out, with the reason</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        </div>
      )}
    </PhoneAppFrame>
  );
}

function Reserve({
  content,
  bank,
  busy,
  run,
}: {
  content: BankContent;
  bank: BankView;
  busy: boolean;
  run: (work: () => Promise<BankReply>) => Promise<boolean>;
}) {
  const [tab, setTab] = useState<Tab>('savings');
  const [text, setText] = useState('');
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const amount = typedPence(text);
  const go = async (work: () => Promise<BankReply>) => {
    if (await run(work)) setText('');
  };
  const open = bank.bonds.filter((bond) => bond.status === 'open');
  const cashed = bank.bonds.filter((bond) => bond.status === 'paid').slice(0, 3);
  const held = open.reduce((sum, bond) => sum + bond.principalPence, 0);
  const room = Math.max(0, content.bondMaxPence - held);
  const canBond =
    amount >= content.minBondPence &&
    amount <= bank.walletPence &&
    amount <= room &&
    open.length < content.maxBonds &&
    bank.bondsLeft > 0;
  return (
    <section className="bank-reserve" aria-label={content.name}>
      <div className="bank-reserve-top">
        <div className="bank-reserve-name">
          <span aria-hidden="true">🏛️</span>
          <span>
            <strong>{content.name}</strong>
            <small>
              Savings {pct(content.savingsBps)} a week · Bonds {pct(content.bondBps)} in {content.bondDays} days
            </small>
          </span>
        </div>
        <p>
          <small>Savings</small>
          <b>{formatPence(bank.savedPence)}</b>
        </p>
        <p>
          <small>In bonds</small>
          <b>{formatPence(bank.bondsPence)}</b>
        </p>
      </div>
      <div className="flights-tabs bank-tabs" role="tablist" aria-label="Savings or bonds">
        {(['savings', 'bonds'] as const).map((item) => (
          <button
            type="button"
            key={item}
            role="tab"
            aria-selected={tab === item}
            className={tab === item ? 'active' : ''}
            onClick={() => setTab(item)}
          >
            {item === 'savings' ? '💰 Savings' : '📜 Bonds'}
          </button>
        ))}
      </div>
      <label className="railco-field">
        <span>AMOUNT</span>
        <input
          inputMode="decimal"
          placeholder="£0"
          value={text}
          aria-label="Amount in pounds"
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="business-chips">
        <button type="button" onClick={() => setText(String(content.minBondPence / 100))}>
          {formatPence(content.minBondPence)}
        </button>
        <button type="button" onClick={() => setText(String(bank.walletPence / 100))}>
          All cash
        </button>
        {tab === 'savings' && bank.savedPence > 0 && (
          <button type="button" onClick={() => setText(String(bank.savedPence / 100))}>
            All savings
          </button>
        )}
      </div>
      {tab === 'savings' ? (
        <>
          <div className="bank-actions">
            <button
              type="button"
              className="city-card-go"
              disabled={busy || amount < content.minMovePence || amount > bank.walletPence || !bank.movesLeft}
              onClick={() => void go(() => api.bank.savings({ action: 'in', pence: amount }, actionKey('bank')))}
            >
              Deposit
            </button>
            <button
              type="button"
              className="flights-terminal"
              disabled={busy || amount < content.minMovePence || amount > bank.savedPence || !bank.movesLeft}
              onClick={() => void go(() => api.bank.savings({ action: 'out', pence: amount }, actionKey('bank')))}
            >
              Withdraw
            </button>
          </div>
          <section className="business-sums" aria-label="Savings">
            <p>
              <span>Savings balance</span>
              <span>{formatPence(bank.savedPence)}</span>
            </p>
            <p>
              <span>Interest rate</span>
              <span>{pct(content.savingsBps)} a week</span>
            </p>
            <p>
              <span>Interest earned</span>
              <span>{formatPence(bank.earnedPence)}</span>
            </p>
            <p className="total">
              <span>This week, about</span>
              <span>+{formatPence(weekInterest(bank.savedPence, content))}</span>
            </p>
            <small>
              Interest builds by the minute and is paid into savings once a day, on the first{' '}
              {formatPence(content.interestCapPence)}. Take your money out any time. Smallest amount{' '}
              {formatPence(content.minMovePence)}. {bank.movesLeft} moves left today.
            </small>
          </section>
        </>
      ) : (
        <>
          <section className="business-sums" aria-label="Bonds">
            <p className="total">
              <span>
                Reserve bond · {content.bondDays} days · {pct(content.bondBps)}
              </span>
            </p>
            <small>
              Your money is locked till it matures, then pays back more. Up to {formatPence(content.bondMaxPence)} in
              bonds at once (room left {formatPence(room)}).
            </small>
            {amount >= content.minBondPence && (
              <p>
                <span>Pays back</span>
                <span>
                  {formatPence(bondPayout(amount, content.bondBps))} on{' '}
                  {londonDay(new Date(now + content.bondDays * 86_400_000))}
                </span>
              </p>
            )}
          </section>
          <button
            type="button"
            className="city-card-go"
            disabled={busy || !canBond}
            onClick={() => void go(() => api.bank.bond({ pence: amount }, actionKey('bond')))}
          >
            {amount >= content.minBondPence
              ? `Buy a bond · ${formatPence(amount)}`
              : `Buy a bond (from ${formatPence(content.minBondPence)})`}
          </button>
          {open.length > 0 && (
            <ul className="bank-bonds" aria-label="Your bonds">
              {open.map((bond) => {
                const due = Date.parse(bond.maturesAt) - now;
                const bought = Date.parse(bond.boughtAt);
                const through = Math.max(
                  0,
                  Math.min(1, (now - bought) / Math.max(1, Date.parse(bond.maturesAt) - bought)),
                );
                return (
                  <li key={bond.id}>
                    <span>
                      <b>{formatPence(bond.principalPence)}</b> → <b>{formatPence(bond.payoutPence)}</b>
                      <small>{due > 0 ? `Matures ${londonWhen(bond.maturesAt)} · ${left(due)} left` : 'Matured'}</small>
                    </span>
                    {due <= 0 && (
                      <button
                        type="button"
                        className="city-card-go"
                        disabled={busy}
                        onClick={() => void run(() => api.bank.cash(bond.id))}
                      >
                        Cash in
                      </button>
                    )}
                    <i aria-hidden="true">
                      <i style={{ width: `${Math.round(through * 100)}%` }} />
                    </i>
                  </li>
                );
              })}
            </ul>
          )}
          {cashed.length > 0 && (
            <p className="business-muted">
              Cashed in: {cashed.map((bond) => formatPence(bond.payoutPence)).join(' · ')}
            </p>
          )}
        </>
      )}
    </section>
  );
}

// the week's bills from everything the player runs, read from each (the home's rent, businesses, a train company)
function WeeklyBills() {
  const [bills, setBills] = useState<Bill[] | null>(null);
  useEffect(() => {
    let live = true;
    void Promise.all([
      api.home.get().catch(() => null),
      api.business.list().catch(() => null),
      api.railco.mine().catch(() => null),
    ]).then(([home, business, railco]) => {
      if (!live) return;
      const list: Bill[] = [];
      if (home && home.home.rent.weeklyPence > 0)
        list.push({
          icon: '🏠',
          label: 'Rent',
          pence: home.home.rent.weeklyPence + home.home.rent.owedPence,
          dueAt: home.home.rent.dueAt,
          from: 'your wallet',
        });
      for (const item of business?.businesses ?? [])
        if (item.status !== 'sold' && item.billsPence > 0)
          list.push({
            icon: '🏢',
            label: item.name,
            pence: item.billsPence,
            dueAt: item.nextBillsAt,
            from: 'its till',
          });
      if (railco?.company && railco.company.bill.total > 0)
        list.push({
          icon: '🚆',
          label: railco.company.name,
          pence: railco.company.bill.total,
          dueAt: railco.company.bill.due,
          from: 'your wallet',
        });
      setBills(list.sort((a, b) => Date.parse(a.dueAt) - Date.parse(b.dueAt)));
    });
    return () => {
      live = false;
    };
  }, []);
  const total = useMemo(() => (bills ?? []).reduce((sum, bill) => sum + bill.pence, 0), [bills]);
  return (
    <section className="business-sums" aria-label="Weekly bills">
      <strong className="business-step-title">🗓️ WEEKLY BILLS</strong>
      {!bills && <small>Adding up the week…</small>}
      {bills && !bills.length && <small>No bills this week.</small>}
      {bills?.map((bill) => (
        <p key={`${bill.label}-${bill.dueAt}`}>
          <span>
            {bill.icon} {bill.label}{' '}
            <small>
              · {londonWhen(bill.dueAt)}, from {bill.from}
            </small>
          </span>
          <span>{formatPence(bill.pence)}</span>
        </p>
      ))}
      {bills && bills.length > 0 && (
        <p className="total">
          <span>This week</span>
          <span>{formatPence(total)}</span>
        </p>
      )}
    </section>
  );
}

type Filter = 'all' | 'in' | 'out';
// the wallet's statement, as Lagos Life's: in, out and now, by day, newest first
function MoneyHistory() {
  const [lines, setLines] = useState<MoneyLine[]>([]);
  const [more, setMore] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const page = useCallback(async (before?: number) => {
    setLoading(true);
    try {
      const reply = await api.bank.history(before);
      setLines((current) => (before ? [...current, ...reply.lines] : reply.lines));
      setMore(reply.more);
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void page();
  }, [page]);
  const shown = lines.filter(
    (line) => filter === 'all' || (filter === 'in' ? line.amountPence > 0 : line.amountPence < 0),
  );
  const sumIn = lines.filter((line) => line.amountPence > 0).reduce((sum, line) => sum + line.amountPence, 0);
  const sumOut = lines.filter((line) => line.amountPence < 0).reduce((sum, line) => sum - line.amountPence, 0);
  const days = new Map<string, MoneyLine[]>();
  for (const line of shown) {
    const day = londonDay(line.at);
    days.set(day, [...(days.get(day) ?? []), line]);
  }
  return (
    <div className="business-detail bank-statement">
      <div className="business-chips" role="radiogroup" aria-label="Show">
        {(['all', 'in', 'out'] as const).map((item) => (
          <button
            type="button"
            key={item}
            role="radio"
            aria-checked={filter === item}
            className={filter === item ? 'active' : ''}
            onClick={() => setFilter(item)}
          >
            {item === 'all' ? 'All' : item === 'in' ? 'In' : 'Out'}
          </button>
        ))}
      </div>
      <p className="business-muted">
        In {formatPence(sumIn)} · Out {formatPence(sumOut)} · Now {formatPence(lines[0]?.balanceAfterPence ?? 0)}
      </p>
      {[...days.entries()].map(([day, items]) => (
        <section key={day} aria-label={day}>
          <strong className="business-step-title">{day.toUpperCase()}</strong>
          <ul>
            {items.map((line) => (
              <li key={line.id}>
                <span>
                  <strong>{line.label}</strong>
                  <small>
                    {londonClock(line.at)}
                    {line.detail ? ` · ${line.detail}` : ''}
                  </small>
                </span>
                <span>
                  <b className={line.amountPence > 0 ? 'up' : 'down'}>
                    {line.amountPence > 0 ? '+' : ''}
                    {formatPence(line.amountPence)}
                  </b>
                  <small>Bal {formatPence(line.balanceAfterPence)}</small>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {!loading && !lines.length && <p className="business-muted">No money has moved yet.</p>}
      {more && (
        <button
          type="button"
          className="flights-terminal"
          disabled={loading}
          onClick={() => void page(lines.at(-1)?.id)}
        >
          {loading ? 'Loading…' : 'Older'}
        </button>
      )}
    </div>
  );
}
